import { Typography } from '@mui/material';
import UPButton from 'components/button';
import UPInput from 'components/input';
import { OnboardingContactStepResponse } from 'constants/OnboardingEnums';
import { OnboardingFlowContext } from 'modules/onboarding/OnboardingFlow';
import { ContactStepFieldNames, OnboardingFlowContactStepFields } from 'modules/onboarding/types';
import React, { useContext, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useAppDispatch } from 'store';
import { sendOnboardingContactStepAction } from 'store/actions/company-onboarding-actions';
import theme from 'theme';
import {
  GENERAL__EMAIL,
  GENERAL__FIRST_NAME,
  GENERAL__LAST_NAME,
  GENERAL__PHONE_NUMBER,
  ONBOARDING__NEXT_STEP,
  ONBOARDING__PREVIOUS_STEP,
  USERS__ALREADY_EXISTS_WITH_DIFFERENT_ROLE,
} from 'translations/constants';
import { getInputErrorText } from 'utils/helpers';
import {
  OnboardingFlowCard,
  OnboardingFlowCardActionsContainer,
  OnboardingFlowCardContentContainer,
  OnboardingFlowCardContentRow,
  OnboardingFlowCardField,
  OnboardingFlowStepContainer,
} from './styles';

const FormDefaultValues: OnboardingFlowContactStepFields = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
};

const ContactStep: React.FC = () => {
  const {
    t,
    activeStep,
    onClickNextStep,
    onClickPreviousStep,
    onboardingData,
    companyIdNumber,
    setOnboardingData,
    setIsExistingManager,
  } = useContext(OnboardingFlowContext);

  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [managerError, setManagerError] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<OnboardingFlowContactStepFields>({
    defaultValues: onboardingData?.contact || FormDefaultValues,
  });

  const onProceed: SubmitHandler<OnboardingFlowContactStepFields> = async data => {
    try {
      setLoading(true);

      const response = await dispatch(sendOnboardingContactStepAction({ data, companyIdNumber })).unwrap();

      if (response === OnboardingContactStepResponse.EXISTS_WITH_DIFFERENT_ROLE) {
        setManagerError(true);
        return;
      }

      setOnboardingData(prev => ({
        ...prev,
        contact: data,
      }));
      onClickNextStep();
      setManagerError(false);

      if (response === OnboardingContactStepResponse.EXISTS_WITH_SAME_ROLE) {
        setIsExistingManager(true);
      } else {
        setIsExistingManager(false);
      }
    } catch (error) {
      console.error('Error submitting contact step:', error);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const onSubmit = () => {
    handleSubmit(onProceed)();
  };

  return (
    <OnboardingFlowStepContainer activeStep={activeStep}>
      <OnboardingFlowCard>
        <OnboardingFlowCardContentContainer>
          <OnboardingFlowCardContentRow container xs={12}>
            <OnboardingFlowCardField item xs={5}>
              <Controller
                name={ContactStepFieldNames.FIRST_NAME}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <UPInput
                    label={t(GENERAL__FIRST_NAME)}
                    value={field.value}
                    placeholder={t(GENERAL__FIRST_NAME)}
                    onChange={field.onChange}
                    required
                    error={!!errors.firstName}
                    helperText={getInputErrorText(errors, ContactStepFieldNames.FIRST_NAME)}
                  />
                )}
              />
            </OnboardingFlowCardField>
            <OnboardingFlowCardField item xs={5}>
              <Controller
                name={ContactStepFieldNames.LAST_NAME}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <UPInput
                    label={t(GENERAL__LAST_NAME)}
                    value={field.value}
                    placeholder={t(GENERAL__LAST_NAME)}
                    onChange={field.onChange}
                    required
                    error={!!errors.lastName}
                    helperText={getInputErrorText(errors, ContactStepFieldNames.LAST_NAME)}
                  />
                )}
              />
            </OnboardingFlowCardField>
          </OnboardingFlowCardContentRow>
          <OnboardingFlowCardContentRow container xs={12}>
            <OnboardingFlowCardField item xs={5}>
              <Controller
                name={ContactStepFieldNames.EMAIL}
                control={control}
                rules={{ required: true, pattern: /^\S+@\S+$/i, maxLength: 49 }}
                render={({ field }) => (
                  <UPInput
                    label={t(GENERAL__EMAIL)}
                    value={field.value}
                    placeholder={t(GENERAL__EMAIL)}
                    onChange={field.onChange}
                    type="email"
                    required
                    error={!!errors.email}
                    helperText={getInputErrorText(errors, ContactStepFieldNames.EMAIL)}
                  />
                )}
              />
            </OnboardingFlowCardField>
            <OnboardingFlowCardField item xs={5}>
              <Controller
                name={ContactStepFieldNames.PHONE_NUMBER}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <UPInput
                    label={t(GENERAL__PHONE_NUMBER)}
                    value={field.value}
                    placeholder={t(GENERAL__PHONE_NUMBER)}
                    onChange={field.onChange}
                    required
                    error={!!errors.phoneNumber}
                    helperText={getInputErrorText(errors, ContactStepFieldNames.PHONE_NUMBER)}
                  />
                )}
              />
            </OnboardingFlowCardField>
          </OnboardingFlowCardContentRow>
        </OnboardingFlowCardContentContainer>
        {managerError && (
          <Typography variant="body1" color="error" ml={theme.spacing(3)}>
            {t(USERS__ALREADY_EXISTS_WITH_DIFFERENT_ROLE)}
          </Typography>
        )}
        <OnboardingFlowCardActionsContainer>
          <UPButton text={t(ONBOARDING__PREVIOUS_STEP)} onClick={onClickPreviousStep} outlined />
          <UPButton text={t(ONBOARDING__NEXT_STEP)} onClick={onSubmit} disabled={loading} />
        </OnboardingFlowCardActionsContainer>
      </OnboardingFlowCard>
    </OnboardingFlowStepContainer>
  );
};

export default ContactStep;
